import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
type SubscriptionType = typeof SUBSCRIPTION_TYPES[number];


interface UpdateSubscriptionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    targetUser: {
        id: string;
        name: string;
        surname: string;
        email: string;
    };
    currentSubscription: string;
    onUpdate: (userId: string, subscription: SubscriptionType) => Promise<void>;
}

const SUBSCRIPTION_TYPES = [
    { value: 'default', label: 'Default', color: 'badge-neutral', priceId:"price_custom_free_monthly" },
    { value: 'premium', label: 'Premium', color: 'badge-success', priceId:"price_1SN1hZPuUOai2Kif56LxICOP" },
    { value: 'platinum', label: 'Platinum', color: 'badge-warning', priceId:"price_custom_platinium_monthly" },
    { value: 'plus', label: 'Plus', color: 'badge-secondary', priceId:"price_1SN1hNPuUOai2KifUh2PI8sx" },
    { value: 'coach', label: 'Coach', color: 'badge-info', priceId:"price_1SN1gtPuUOai2KifagtWdj6B" },
];

const UpdateSubscriptionDialog: React.FC<UpdateSubscriptionDialogProps> = ({
    isOpen,
    onClose,
    targetUser,
    currentSubscription,
    onUpdate,
}) => {
    const [selectedSubscription, setSelectedSubscription] = useState(currentSubscription);
    const [isUpdating, setIsUpdating] = useState(false);

    if (!isOpen) return null;

    const handleUpdate = async () => {
    if (selectedSubscription === currentSubscription) {
        alert('Please select a different subscription type.');
        return;
    }

    setIsUpdating(true);
    try {
        // Convert value to full object before calling onUpdate
        const subscriptionObj = SUBSCRIPTION_TYPES.find(
        (sub) => sub.value === selectedSubscription
        );

        if (!subscriptionObj) {
        throw new Error('Invalid subscription selected');
        }

        await onUpdate(targetUser.id, subscriptionObj); // now full object
        onClose();
    } catch (error) {
        console.error('Error updating subscription:', error);
        alert(
        `Failed to update subscription: ${
            error instanceof Error ? error.message : 'Unknown error'
        }`
        );
    } finally {
        setIsUpdating(false);
    }
    };


    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end md:items-center justify-center min-h-screen p-0 md:p-4 text-center">
                {/* Background overlay */}
                <div 
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
                    onClick={onClose}
                />

                {/* Center modal */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b">
                        <h3 className="text-lg font-heading text-gray-900">
                            Update Admin Subscription
                        </h3>
                        <button
                            onClick={onClose}
                            disabled={isUpdating}
                        >
                            <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-700" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="px-6 py-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">New Subscription Type</span>
                            </label>
                            <div className="space-y-2">
                                {SUBSCRIPTION_TYPES.map((subscription) => (
                                <label
                                    key={subscription.value}
                                    className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                                >
                                    <input
                                        type="radio"
                                        name="subscription"
                                        value={subscription.value}
                                        checked={selectedSubscription === subscription.value}
                                        onChange={(e) => setSelectedSubscription(e.target.value)}
                                        className="radio radio-primary"
                                        disabled={isUpdating}
                                    />
                                    <span className={`inline-flex px-2 py-1 text-xs rounded-full capitalize ${subscription.color}`}>
                                        {subscription.label}
                                    </span>
                                    {subscription.value === currentSubscription && (
                                        <span className="text-xs text-gray-500">(current)</span>
                                    )}
                                </label>
                                ))}
                            </div>
                        </div>

                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-800">
                                <strong>Note:</strong> This will update the admin subscription and automatically 
                                recalculate the user's active subscription based on all available subscription sources.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end space-x-2 px-6 py-4 border-t">
                        <button
                        onClick={onClose}
                        className="btn btn-ghost"
                        disabled={isUpdating}
                        >
                        Cancel
                        </button>
                        <button
                        onClick={handleUpdate}
                        className="btn btn-primary !shadow-none"
                        disabled={isUpdating || selectedSubscription === currentSubscription}
                        >
                        {isUpdating ? (
                            <>
                            <span className="loading loading-spinner loading-sm mr-2"></span>
                            Updating...
                            </>
                        ) : (
                            'Update Subscription'
                        )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateSubscriptionDialog;

